<template>
  <provider-show-section class="provider_show_tabs_section">
    <v-card flat class="provider_show_tabs_card">
      <v-tabs
        v-model="localTab"
        class="provider_show_tabs"
        background-color="transparent"
        :color="accentColor"
        grow
      >
        <v-tab>{{ $t("MESSAGES.centerStaff") }}</v-tab>
        <v-tab>{{ $t("MESSAGES.centerTransactions") }}</v-tab>
      </v-tabs>

      <div v-show="localTab === 0" class="provider_show_tab_panel">
        <v-data-table
          class="thumb provider_show_table"
          :headers="staffHeaders"
          :items="staff"
          :items-per-page="10"
          hide-default-footer
          :no-data-text="$t('TABLES.noData')"
        >
          <template v-slot:[`item.id`]="{ index }">
            {{ index + 1 }}
          </template>

          <template v-slot:[`item.image`]="{ item }">
            <span v-if="hasStaffImage(item)">
              <button
                type="button"
                class="provider_show_staff_thumb"
                @click="$emit('preview', item.image)"
              >
                <img loading="lazy" :src="item.image" alt="staff" /></button
            ></span>
            <span v-else class="provider_show_table_empty">-</span>
          </template>

          <template v-slot:[`item.mobile`]="{ item }">
            <span class="provider_show_value--ltr">
              {{ formatStaffMobile(item) }}
            </span>
          </template>

          <template v-slot:[`item.attendance_status`]="{ item }">
            {{ item._attendance_status || item.attendance_status || "-" }}
          </template>

          <template v-slot:[`item.work_schedule`]="{ item }">
            <div class="provider_show_schedule">
              <template v-if="getWorkScheduleLines(item).length">
                <div
                  v-for="(line, idx) in getWorkScheduleLines(item)"
                  :key="'ws-' + item.id + '-' + idx"
                  class="provider_show_schedule__line"
                >
                  {{ line }}
                </div>
              </template>
              <span v-else class="provider_show_table_muted">
                {{ $t("MESSAGES.no_working_hours") }}
              </span>
            </div>
          </template>

          <template v-slot:[`item.is_active`]="{ item }">
            <span
              class="provider_show_badge"
              :class="item.is_active ? 'is_active' : 'is_inactive'"
            >
              {{
                item.is_active
                  ? $t("PLACEHOLDERS.active")
                  : $t("PLACEHOLDERS.notActive")
              }}
            </span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div
              class="actions"
              v-if="$can('festivalproviders activate', 'festivalproviders')"
            >
              <a-tooltip placement="bottom" v-if="!item.is_active">
                <template slot="title">
                  <span>{{ $t("MESSAGES.activateEmployeeHint") }}</span>
                </template>
                <span>
                  <button
                    class="btn_activate"
                    type="button"
                    :disabled="actionLoadingId === item.id"
                    @click="$emit('activate-staff', item)"
                  >
                    <i class="fad fa-check-circle"></i>
                  </button>
                </span>
              </a-tooltip>

              <a-tooltip placement="bottom" v-else>
                <template slot="title">
                  <span>{{ $t("BUTTONS.deactivate") }}</span>
                </template>
                <span>
                  <button
                    class="btn_deactivate"
                    type="button"
                    :disabled="actionLoadingId === item.id"
                    @click="openDeactivateModal(item)"
                  >
                    <i class="fad fa-times-circle"></i>
                  </button>
                </span>
              </a-tooltip>
            </div>
            <i
              v-else
              class="fal fa-lock-alt fs-5 provider_show_table_muted"
            ></i>
          </template>
        </v-data-table>
      </div>

      <div v-show="localTab === 1" class="provider_show_tab_panel">
        <v-data-table
          class="thumb provider_show_table"
          :headers="transactionHeaders"
          :items="transactions"
          :items-per-page="10"
          hide-default-footer
          :no-data-text="$t('TABLES.noData')"
        >
          <template v-slot:[`item.id`]="{ index }">
            {{ index + 1 }}
          </template>

          <template v-slot:[`item.amount`]="{ item }">
            <span class="provider_show_value--ltr">
              {{ item.amount }} {{ $t("PLACEHOLDERS.riyal") }}
            </span>
          </template>

          <template v-slot:[`item.balance_after`]="{ item }">
            <span class="provider_show_value--ltr">
              {{ item.balance_after }} {{ $t("PLACEHOLDERS.riyal") }}
            </span>
          </template>

          <template v-slot:[`item.date`]="{ item }">
            <span class="provider_show_value--ltr">{{ item.date || "-" }}</span>
          </template>

          <template v-slot:[`item.time`]="{ item }">
            <span class="provider_show_value--ltr">{{ item.time || "-" }}</span>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <v-dialog
      v-model="dialogDeactivate"
      max-width="520"
      content-class="provider_show_dialog"
    >
      <v-card class="provider_show_dialog_card">
        <v-card-title
          class="provider_show_dialog_title"
          v-if="staffToDeactivate"
        >
          {{
            $t("TITLES.DeactivateConfirmingMessage", {
              name: staffToDeactivate.name,
            })
          }}
        </v-card-title>
        <v-card-text>
          <base-input
            col="12"
            rows="3"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.deactivateReason')"
            v-model.trim="deactivateReason"
            required
          />
        </v-card-text>
        <v-card-actions class="provider_show_dialog_actions">
          <v-btn
            class="modal_confirm_btn"
            :disabled="!deactivateReason || deactivateReason.length < 3"
            :loading="
              actionLoadingId === (staffToDeactivate && staffToDeactivate.id)
            "
            @click="confirmDeactivate"
          >
            {{ $t("BUTTONS.ok") }}
          </v-btn>
          <v-btn class="modal_cancel_btn" @click="closeDeactivateModal">
            {{ $t("BUTTONS.cancel") }}
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </provider-show-section>
</template>

<script>
import ProviderShowSection from "./ProviderShowSection.vue";
import {
  formatStaffMobile,
  formatWorkSchedule,
  hasStaffImage,
} from "@/utils/festivalProviderShow";

export default {
  name: "ProviderShowTables",
  components: { ProviderShowSection },
  props: {
    staff: { type: Array, default: () => [] },
    transactions: { type: Array, default: () => [] },
    actionLoadingId: { type: [Number, String], default: null },
    accentColor: { type: String, default: "#405950" },
    activeTab: { type: Number, default: 0 },
  },
  data() {
    return {
      localTab: 0,
      dialogDeactivate: false,
      staffToDeactivate: null,
      deactivateReason: null,
    };
  },
  computed: {
    staffHeaders() {
      return [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          sortable: false,
          align: "center",
          width: "70px",
        },
        {
          text: this.$t("MESSAGES.employeeImage"),
          value: "image",
          sortable: false,
          align: "center",
          width: "90px",
        },
        {
          text: this.$t("MESSAGES.employeeName"),
          value: "name",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.mobileNumber"),
          value: "mobile",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.attendanceStatus"),
          value: "attendance_status",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.workDaysAndHours"),
          value: "work_schedule",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "is_active",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "110px",
        },
      ];
    },
    transactionHeaders() {
      return [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          sortable: false,
          align: "center",
          width: "70px",
        },
        {
          text: this.$t("MESSAGES.settlementAdminName"),
          value: "admin_name",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.settlementAmount"),
          value: "amount",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.balanceAfterSettlement"),
          value: "balance_after",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.settlementDate"),
          value: "date",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.settlementTime"),
          value: "time",
          sortable: false,
        },
      ];
    },
  },
  watch: {
    activeTab(value) {
      this.localTab = value;
    },
    localTab(value) {
      this.$emit("update:activeTab", value);
    },
  },
  methods: {
    hasStaffImage,
    formatStaffMobile,
    getWorkScheduleLines(item) {
      return formatWorkSchedule(item.work_schedule, this.$t.bind(this));
    },
    openDeactivateModal(item) {
      this.staffToDeactivate = item;
      this.deactivateReason = null;
      this.dialogDeactivate = true;
    },
    closeDeactivateModal() {
      this.dialogDeactivate = false;
      this.staffToDeactivate = null;
      this.deactivateReason = null;
    },
    confirmDeactivate() {
      if (!this.staffToDeactivate) return;
      if (!this.deactivateReason || this.deactivateReason.length < 3) return;
      this.$emit("deactivate-staff", {
        item: this.staffToDeactivate,
        reason: this.deactivateReason,
      });
    },
  },
};
</script>
